import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "./api";
import {
  TrendingUp,
  Package,
  AlertTriangle,
  Truck,
  Award,
  IndianRupee,
  Wallet,
  Receipt,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const KPI = ({ icon: Icon, label, value, sub, accent = "#D4AF37", to, testid }) => {
  const cls = `block bg-ink-900 border border-white/5 p-6 space-y-3 transition-colors ${to ? "hover:border-gold/40 hover:bg-ink-900/80 cursor-pointer" : ""}`;
  const tid = testid || `kpi-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <p className="label-eyebrow text-mist">{label}</p>
        <Icon className="w-4 h-4 stroke-[1.5]" style={{ color: accent }} />
      </div>
      <p className="num-display text-3xl text-bone">{value}</p>
      {sub && <p className="text-xs text-mist font-light">{sub}</p>}
    </>
  );
  return to ? (
    <Link to={to} className={cls} data-testid={tid}>{inner}</Link>
  ) : (
    <div className={cls} data-testid={tid}>{inner}</div>
  );
};

const ACCENT_PALETTE = ["#D4AF37", "#3B82F6", "#A855F7", "#EF4444", "#F59E0B", "#00D4C4", "#E8E8E8"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([adminApi.get("/dashboard/stats"), adminApi.get("/dashboard/charts")])
      .then(([s, c]) => {
        setStats(s.data);
        setCharts(c.data);
      })
      .catch((e) => setErr(e.message));
  }, []);

  if (err) return <p className="text-rose-300">{err}</p>;
  if (!stats || !charts) return <p className="text-mist">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard-page">
      <div>
        <p className="label-eyebrow text-gold">Today · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
        <h1 className="font-serif text-4xl text-bone mt-1">Operations Dashboard</h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPI icon={TrendingUp} label="Today's Orders" value={stats.today_orders} accent="#D4AF37" />
        <KPI icon={IndianRupee} label="Monthly Revenue" value={`₹${Math.round(stats.monthly_revenue).toLocaleString("en-IN")}`} accent="#00D4C4" />
        <KPI icon={Package} label="Available Stock" value={stats.available_stock.toLocaleString("en-IN")} sub="units across all SKUs" accent="#3B82F6" />
        <KPI icon={AlertTriangle} label="Low Stock Alerts" value={stats.low_stock_count} sub="SKUs below threshold" accent="#EF4444" to="/admin/inventory?lowStock=1" />
        <KPI icon={Award} label="Top Selling" value={stats.top_selling?.name || "—"} sub={stats.top_selling ? `${stats.top_selling.units} units` : "no orders yet"} accent="#A855F7" />
        <KPI icon={Truck} label="Pending Shipments" value={stats.pending_shipments} sub="to be packed/shipped" accent="#F59E0B" />
      </div>

      {/* Channel partner cashflow band */}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="channel-partner-cashflow">
        <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-400/30 p-6" data-testid="collections-tile">
          <div className="flex items-center justify-between">
            <p className="label-eyebrow text-emerald-400 flex items-center gap-2"><Wallet className="w-3.5 h-3.5" /> Collections this month</p>
            <p className="text-emerald-400/60 text-[10px] label-eyebrow">channel partners</p>
          </div>
          <p className="num-display text-4xl text-emerald-300 mt-3">₹{Math.round(stats.collections_this_month || 0).toLocaleString("en-IN")}</p>
          <p className="text-mist text-xs font-light mt-1">{stats.collections_count || 0} payment{(stats.collections_count || 0) === 1 ? "" : "s"} received from distributors</p>
        </div>
        <div className={`bg-gradient-to-br ${stats.overdrawn_distributors > 0 ? "from-burgundy/15" : "from-gold/10"} to-transparent border ${stats.overdrawn_distributors > 0 ? "border-burgundy/40" : "border-gold/30"} p-6`} data-testid="outstanding-tile">
          <div className="flex items-center justify-between">
            <p className={`label-eyebrow flex items-center gap-2 ${stats.overdrawn_distributors > 0 ? "text-rose-300" : "text-gold"}`}>
              <Receipt className="w-3.5 h-3.5" /> Outstanding total
            </p>
            {stats.overdrawn_distributors > 0 && (
              <p className="text-rose-300 text-[10px] label-eyebrow flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {stats.overdrawn_distributors} overdrawn</p>
            )}
          </div>
          <p className={`num-display text-4xl mt-3 ${stats.overdrawn_distributors > 0 ? "text-rose-300" : "text-bone"}`}>₹{Math.round(stats.outstanding_total || 0).toLocaleString("en-IN")}</p>
          <p className="text-mist text-xs font-light mt-1">parked with channel partners</p>
        </div>
      </div>

      {/* Low stock alerts */}
      {stats.low_stock_items?.length > 0 && (
        <div className="bg-ink-900 border border-burgundy/40 p-6" data-testid="low-stock-alerts">
          <div className="flex items-center justify-between mb-3">
            <p className="label-eyebrow text-rose-300 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Low Stock Attention
            </p>
            <Link to="/admin/inventory?lowStock=1" className="label-eyebrow text-mist hover:text-gold text-[10px]" data-testid="low-stock-view-all">
              View all →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.low_stock_items.map((p) => (
              <Link
                key={p.sku}
                to={`/admin/inventory?sku=${encodeURIComponent(p.sku)}&restock=1`}
                className="block text-sm border border-rose-300/20 border-l-[3px] border-l-rose-300 bg-rose-300/[0.03] hover:bg-rose-300/[0.08] hover:border-rose-300/40 pl-4 pr-3 py-3 -m-0 transition-colors"
                data-testid={`low-stock-${p.sku}`}
              >
                <p className="text-bone font-light flex items-center justify-between">
                  {p.name}
                  <span className="text-rose-300 text-[10px] label-eyebrow opacity-0 group-hover:opacity-100">Restock →</span>
                </p>
                <p className="text-mist text-xs mt-0.5">
                  <span className="num-display text-rose-300">{p.stock}</span>
                  <span className="text-mist/60"> / threshold {p.threshold}</span>
                </p>
                <p className="text-rose-300 text-[10px] label-eyebrow mt-1.5">Tap to restock →</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Charts grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue 7-day */}
        <div className="bg-ink-900 border border-white/5 p-6">
          <p className="label-eyebrow text-gold mb-4">Revenue · last 7 days</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={charts.revenue_7d}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" stroke="#A39D98" fontSize={11} />
              <YAxis stroke="#A39D98" fontSize={11} />
              <Tooltip contentStyle={{ background: "#161515", border: "1px solid #D4AF37", color: "#F9F6F0" }} />
              <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} dot={{ fill: "#D4AF37", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Order status pie */}
        <div className="bg-ink-900 border border-white/5 p-6">
          <p className="label-eyebrow text-gold mb-4">Order Status Distribution</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={charts.order_status.filter((s) => s.count > 0)}
                dataKey="count"
                nameKey="status"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={3}
              >
                {charts.order_status.map((_, i) => (
                  <Cell key={i} fill={ACCENT_PALETTE[i % ACCENT_PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "#161515", border: "1px solid #D4AF37", color: "#F9F6F0" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#A39D98" }} />
            </PieChart>
          </ResponsiveContainer>
          {charts.order_status.every((s) => s.count === 0) && (
            <p className="text-mist text-xs text-center font-light italic mt-3">No orders yet</p>
          )}
        </div>

        {/* Sales by variant */}
        <div className="bg-ink-900 border border-white/5 p-6">
          <p className="label-eyebrow text-gold mb-4">Sales by Variant · this month</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.sales_by_variant.slice(0, 6)}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="#A39D98" fontSize={10} />
              <YAxis stroke="#A39D98" fontSize={11} />
              <Tooltip contentStyle={{ background: "#161515", border: "1px solid #D4AF37", color: "#F9F6F0" }} />
              <Bar dataKey="units" fill="#D4AF37" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {stats.sales_by_variant.length === 0 && (
            <p className="text-mist text-xs text-center font-light italic mt-3">No sales data yet</p>
          )}
        </div>

        {/* Inventory by warehouse */}
        <div className="bg-ink-900 border border-white/5 p-6">
          <p className="label-eyebrow text-gold mb-4">Inventory by Warehouse</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.inventory_by_warehouse} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" stroke="#A39D98" fontSize={11} />
              <YAxis type="category" dataKey="warehouse" stroke="#A39D98" fontSize={11} width={100} />
              <Tooltip contentStyle={{ background: "#161515", border: "1px solid #D4AF37", color: "#F9F6F0" }} />
              <Bar dataKey="units" fill="#3B82F6" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
